import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fileUrl, formatFCFA } from "@/lib/api";
import { trackInitiateCheckout } from "@/lib/pixel";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, removeItem, updateQuantity, subtotal } = useCart();

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="label-tiny mb-3 flex items-center gap-3">
        <span className="gold-line" /> Votre Sélection
      </div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-12">Panier</h1>

      {items.length === 0 ? (
        <div className="border border-black/5 py-20 text-center" data-testid="empty-cart">
          <div className="text-5xl text-[#B8860B]/40 font-display mb-4">✟</div>
          <p className="text-[#6B6B6B]">Votre panier est vide.</p>
          <Link to="/catalogue" className="btn-primary mt-8 inline-flex">
            Voir le catalogue
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-4">
            {items.map((it) => (
              <div
                key={it.key}
                data-testid={`cart-item-${it.product_id}`}
                className="flex gap-4 border border-black/5 p-4 bg-white"
              >
                <div className="w-24 h-24 bg-[#FAF7F0] flex-shrink-0 border border-black/5">
                  {it.photo ? (
                    <img src={fileUrl(it.photo)} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#B8860B]/50 font-display text-2xl">✟</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight">{it.name}</div>
                  {it.variant_name && <div className="text-xs text-[#6B6B6B] mt-1">{it.variant_name}</div>}
                  <div className="text-[#B8860B] mt-2 text-sm">{formatFCFA(it.price)}</div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeItem(it.key)}
                    data-testid={`cart-remove-${it.product_id}`}
                    className="text-[#6B6B6B] hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="flex items-center border border-black/10">
                    <button onClick={() => updateQuantity(it.key, it.quantity - 1)} className="px-3 py-1 text-[#B8860B]">−</button>
                    <span className="px-3 text-sm">{it.quantity}</span>
                    <button onClick={() => updateQuantity(it.key, it.quantity + 1)} className="px-3 py-1 text-[#B8860B]">+</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border border-[#B8860B]/30 bg-white p-8 h-fit">
            <h3 className="font-display text-xl text-[#B8860B] mb-6">Récapitulatif</h3>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#6B6B6B]">Sous-total</span>
              <span data-testid="cart-subtotal">{formatFCFA(subtotal)}</span>
            </div>
            <p className="text-xs text-[#6B6B6B] mb-6 italic">
              Frais de livraison calculés lors du paiement.
            </p>
            <Link
              to="/checkout"
              className="btn-primary w-full"
              data-testid="cart-checkout-btn"
              onClick={() => trackInitiateCheckout({ items, value: subtotal })}
            >
              Passer à la commande
            </Link>
            <Link to="/catalogue" className="block text-center mt-4 text-xs uppercase tracking-widest text-[#6B6B6B] hover:text-[#B8860B]">
              Continuer mes achats
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
